import React, { useState, useEffect } from "react";
import "./robot.scss";
import PlantHeader from "../plantHeader/plantHeader";
import Menu from "../menu/menu";
import leftArrowIcon from "./left-arrow.png";
import rightArrowIcon from "./right-arrow.png";
import {
  LOCAL_STORAGE_TOKEN_KEY,
  SOCKET_URL,
  LOCAL_STORAGE_USER_KEY,
  LOCAL_STORAGE_PROJECT,
  LOCAL_STORAGE_ROBOT,
} from "../../consts";
import {
  getCurrentProject,
  redirectIfNoProject,
  getRobotIdFromUrl,
  setRobotIdInUrl,
} from "../../utils/projectUtils";
import "react-dropdown/style.css";
import "react-datepicker/dist/react-datepicker.css";
import Loader from "../loader/loader";
import CleaningControlRobot from "../cleaningControl/cleaningControlRobot";
import EditError from "../editError/editError";
import socketIOClient from "socket.io-client";
import { TimeZone } from "../utils";
import RobotLogs from "./logs";
import ErrorTable from "../dashboard/errors-table";
import RobotSettings from "./robot-settings";
import ApiHandler from "../api";
import { useNavigate } from "react-router";
import { RobotProvider, useRobot } from "./RobotContext";

const RobotComponent = () => {
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const { robot, setRobot } = useRobot();
  const [isLoading, setIsLoading] = useState(false);
  const [showEditError, setShowEditError] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [socket, setSocket] = useState(null);

  const api = new ApiHandler();
  const navigate = useNavigate();

  // Initialize component
  useEffect(() => {
    // Check if we need to redirect due to missing project
    if (redirectIfNoProject(navigate)) {
      return;
    }

    const initializeRobot = async () => {
      // Get initial data from localStorage or props
      const storedUser = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_USER_KEY) || "null"
      );
      const storedProject = getCurrentProject();
      const robotIdFromUrl = getRobotIdFromUrl();

      if (!storedProject) {
        navigate("/projects");
        return;
      }

      setUser(storedUser);
      setProject(storedProject);

      let robotToUse = null;

      // Try to get robot from URL parameter first
      if (robotIdFromUrl) {
        try {
          robotToUse = await api.getRobotDetails(robotIdFromUrl);
          if (robotToUse) {
            // Update localStorage with robot from URL
            localStorage.setItem(
              LOCAL_STORAGE_ROBOT,
              JSON.stringify(robotToUse)
            );
          }
        } catch (error) {
          console.error("Error fetching robot from URL:", error);
        }
      }

      // Fallback to localStorage if URL robot not found
      if (!robotToUse) {
        const storedRobot = JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_ROBOT) || "null"
        );
        if (storedRobot) {
          robotToUse = storedRobot;
          // Update URL with robot ID from localStorage
          setRobotIdInUrl(storedRobot._id, true);
        }
      }

      // If no robot found, redirect to robots list
      if (!robotToUse) {
        navigate("/robots");
        return;
      }

      connectToSocket(robotToUse);
      getRobotData(robotToUse, storedProject);
    };

    initializeRobot();
  }, []);

  const connectToSocket = (robotData) => {
    const socketInstance = socketIOClient(SOCKET_URL);

    socketInstance.on("connect", () => {
      const authToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
      socketInstance.emit("SET_USER_CONNECTED_TO_ROBOT", {
        token: authToken,
        robotId: robotData._id,
      });
    });

    socketInstance.on("disconnect", () => {
      console.log("disconnect form socket");
    });

    socketInstance.on("REFRESH", () => {
      console.log("Refreshing robot data");
      getRobotData(robotData, project);
    });

    setSocket(socketInstance);
  };

  const getRobotData = async (robotData, currentProject = null) => {
    try {
      setIsLoading(true);

      // Get robot details
      const robotDetails = await api.getRobotDetails(robotData._id);
      setRobot(robotDetails);

      // Get errors - use current project or stored project
      const projectToUse = currentProject || project || getCurrentProject();
      if (projectToUse) {
        const errors = await api.getErrors({
          projectId: projectToUse._id,
          robotId: robotData._id,
        });
      }
    } catch (error) {
      console.error("Error fetching robot data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const robotClicked = (newRobot) => {
    console.log("robotSelected", newRobot);

    if (socket) {
      socket.disconnect();
    }

    connectToSocket(newRobot);

    // Update robot selection
    const updatedRobot = {
      ...newRobot,
      robotId: newRobot._id,
      robotName: newRobot.robotNumber.toString(),
    };

    setRobot(updatedRobot);
    localStorage.setItem(LOCAL_STORAGE_ROBOT, JSON.stringify(updatedRobot));

    // Update URL with new robot ID
    setRobotIdInUrl(newRobot._id, true);

    getRobotData(newRobot, project);
  };

  const handleShowEditError = (error) => {
    setShowEditError(true);
    setSelectedError(error);
  };

  // Cleanup socket on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  if (!project || !robot) {
    return <Loader />;
  }

  // Construct URLs with project parameters
  const robotsUrl = project?._id ? `/robots?project=${project._id}` : "/robots";
  const dashboardUrl = project?._id
    ? `/dashboard?project=${project._id}`
    : "/dashboard";

  return (
    <div className="page">
      {isLoading ? <Loader /> : null}
      <div className="menu-wrapper">
        <Menu />
      </div>
      {showEditError ? (
        <EditError
          closeEditError={() => {
            setShowEditError(false);
          }}
          errors={[selectedError]}
        />
      ) : null}
      <div className="content">
        <PlantHeader
          title={
            `Robot ${robot.robotName}` + (robot.rpi ? ` (${robot.rpi})` : "")
          }
          secondaryText={
            project?.arrayType === "fixed" ? "Robots" : "Robots & Vehicles"
          }
          secondaryLink={robotsUrl}
          thirdText={project?.projectName}
          thirdLink={dashboardUrl}
        />
        <div className="arrows">
          {robot.prev ? (
            <span className="item" onClick={() => robotClicked(robot.prev)}>
              <span className="robot-name">{robot.prev.robotNumber}</span>
              <img src={leftArrowIcon} />
            </span>
          ) : null}
          <span className="dotdotdot">...</span>
          {robot.next ? (
            <span className="item" onClick={() => robotClicked(robot.next)}>
              <img src={rightArrowIcon} />
              <span className="robot-name">{robot.next.robotNumber}</span>
            </span>
          ) : null}
        </div>
        <div className="robots">
          <div className="left">
            {project?.arrayType !== "solar-tracking" ? (
              <div className="control-panel">
                <div>
                  <div className="title">CONTROL PANEL</div>
                  <div className="status">
                    {project?.connectionStatus === "offline" ? (
                      <span className="offline">
                        <span className="circle"></span>offline
                      </span>
                    ) : (
                      <span className="online">
                        <span className="circle"></span>online
                      </span>
                    )}
                  </div>
                </div>
                <CleaningControlRobot
                  autoMode={true}
                  offline={false}
                  category="robot"
                  startCleaningSeconds={robot.startCleaningSeconds}
                  currentState={robot.currentStatus}
                  categoryValue={robot.robotName}
                  categoryId={robot.id}
                  onPendingCommand={(pendingCommand) =>
                    setRobot({ ...robot, pendingCommand })
                  }
                  robot={robot}
                  project={project}
                  user={user}
                  namespace="robot"
                />
              </div>
            ) : null}
            <RobotSettings project={project} user={user} />
          </div>

          <div className="right-column">
            <ErrorTable
              project={project}
              user={user}
              showEditError={handleShowEditError}
              robot={robot}
            />
          </div>

          <RobotLogs project={project} />
        </div>
      </div>
    </div>
  );
};

// Wrapper component that provides the robot context
const RobotWithContext = () => {
  return (
    <RobotProvider>
      <RobotComponent />
    </RobotProvider>
  );
};

export default RobotWithContext;
